import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, MoreHorizontal, Pencil, Phone, Trash2 } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/DropdownMenu';
import { DeleteStudentDialog } from './DeleteStudentDialog';
import { useStudents } from '../../contexts/StudentsContext';
import type { Student } from '../../types/student';
import { initialsOf, orDash, relativeTime, truncate } from '../../utils/format';
const HEADERS = ['Full names', 'Gender', 'Class', 'Father', 'Mother', 'Note', ''];
function Monogram({
  student,
  size = 'md'
}: {
  student: Student;
  size?: 'md' | 'lg';
}) {
  return <span aria-hidden="true" className={`grid shrink-0 place-items-center rounded-xl border border-[var(--glass-hairline)] bg-white/60 font-semibold text-muted-foreground dark:bg-white/5 ${size === 'lg' ? 'h-11 w-11 text-xs' : 'h-9 w-9 text-[11px]'}`} data-id="element-343" data-mp="2wfcvyiqy2c5y">
      {student.fullNames.trim() ? initialsOf(student.fullNames) : '—'}
    </span>;
}
function GenderBadge({
  student
}: {
  student: Student;
}) {
  if (!student.gender) return <span className="text-sm text-muted-foreground" data-id="element-344" data-mp="2t1iojicsgm9x">—</span>;
  const tone = student.gender === 'Male' ? 'bg-sky-500/12 text-sky-700 dark:text-sky-300' : student.gender === 'Female' ? 'bg-pink-500/12 text-pink-700 dark:text-pink-300' : 'bg-violet-500/12 text-violet-700 dark:text-violet-300';
  return <Badge className={`rounded-full border-0 ${tone}`} data-id="element-345" data-mp="3qngbpt245fs7">{student.gender}</Badge>;
}
function RowActions({
  student
}: {
  student: Student;
}) {
  const {
    openDetail,
    openEditForm
  } = useStudents();
  const [deleting, setDeleting] = useState<Student | null>(null);
  return <>
      <DropdownMenu data-id="element-346" data-mp="20b0nsnzjewzo">
        <DropdownMenuTrigger asChild data-id="element-347" data-mp="20ah6n9hpocmp">
          <button type="button" aria-label={`Actions for ${student.fullNames || 'this record'}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-white/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:hover:bg-white/10" data-id="element-348" data-mp="25amwql8c1r2o">
            <MoreHorizontal className="h-4 w-4" data-id="element-349" data-mp="2005rqdf07jg" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44" data-id="element-350" data-mp="3t6tfjim9xezl">
          <DropdownMenuItem onClick={() => openDetail(student)} data-id="element-351" data-mp="3oacg3s9mje77">
            <Eye className="mr-2 h-4 w-4" data-id="element-352" data-mp="3k7g6gr22asfe" />
            View details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openEditForm(student)} data-id="element-353" data-mp="3f8cz2zym0a49">
            <Pencil className="mr-2 h-4 w-4" data-id="element-354" data-mp="3faz8l5528zp4" />
            Edit record
          </DropdownMenuItem>
          <DropdownMenuSeparator data-id="element-355" data-mp="3abw9007unloj" />
          <DropdownMenuItem variant="destructive" onClick={() => setDeleting(student)} data-id="element-356" data-mp="1di1i3fyraj7c">
            <Trash2 className="mr-2 h-4 w-4" data-id="element-357" data-mp="18ion27xqe5to" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteStudentDialog student={deleting} onClose={() => setDeleting(null)} data-id="element-358" data-mp="1g1ep1ee7qyiy" />
    </>;
}
function ParentCell({
  name,
  phone
}: {
  name: string;
  phone: string;
}) {
  return <div className="min-w-0" data-id="element-359" data-mp="1pssip5jhh446">
      <p className="truncate text-sm text-foreground" data-id="element-360" data-mp="1psj2uas5kdc7">{orDash(name)}</p>
      {phone.trim() ? <p className="mt-0.5 flex items-center gap-1 truncate font-mono text-xs text-muted-foreground" data-id="element-361" data-mp="1uq2h3aouoq45">
          <Phone className="h-3 w-3 shrink-0" data-id="element-362" data-mp="x4wnk59kbk9i" />
          {phone}
        </p> : <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-363" data-mp="122op7r00ekqw">No telephone</p>}
    </div>;
}
export function StudentTable({
  students
}: {
  students: Student[];
}) {
  const {
    openDetail
  } = useStudents();
  return <>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto scrollbar-slim md:block" data-id="element-364" data-mp="2igy5dxu3l36n">
        <table className="w-full min-w-[900px] border-collapse text-left" data-id="element-365" data-mp="2nfhrpzclsgzj">
          <thead data-id="element-366" data-mp="2ndolum9te0sl">
            <tr className="border-b border-[var(--glass-hairline)]" data-id="element-367" data-mp="2nhcfdlf9en7b">
              {HEADERS.map(header => <th key={header || 'actions'} scope="col" className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-368" data-mp="2gziysqtngrg6">
                  {header}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-369" data-mp="33yx9j01yapgw">
            {students.map((student, index) => <motion.tr key={student.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            delay: Math.min(index * 0.02, 0.2)
          }} className="group border-b border-[var(--glass-hairline)] transition-colors last:border-0 hover:bg-white/50 dark:hover:bg-white/5" data-id="element-370" data-mp="2zy3lm6oidao0" data-mpi={index}>
                <td className="max-w-[260px] px-4 py-3" data-id="element-371" data-mp="3ioleqcd9689w">
                  <div className="flex items-center gap-3" data-id="element-372" data-mp="3iq67pxjx5j5n">
                    <Monogram student={student} data-id="element-373" data-mp="64hd1acpombr" />
                    <div className="min-w-0" data-id="element-374" data-mp="65sc4wv7p5wj">
                      <button type="button" onClick={() => openDetail(student)} className="block max-w-full truncate text-left text-sm font-semibold text-foreground hover:text-brand focus-visible:outline-none focus-visible:underline" data-id="element-375" data-mp="a7461el4cdaj">
                        {orDash(student.fullNames)}
                      </button>
                      <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-376" data-mp="3nde4nqu7tyka">Added {relativeTime(student.createdAtMs)}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-377" data-mp="3ekasrmz7xtrr">
                  <GenderBadge student={student} data-id="element-378" data-mp="3el2co0jge327" />
                </td>
                <td className="px-4 py-3" data-id="element-379" data-mp="2zol1b9b9kofn">
                  <span className="text-sm text-foreground" data-id="element-380" data-mp="2zpmmw7pwotbm">{orDash(student.className)}</span>
                </td>
                <td className="max-w-[180px] px-4 py-3" data-id="element-381" data-mp="2li18fmvgvuog">
                  <ParentCell name={student.fatherName} phone={student.fatherPhone} data-id="element-382" data-mp="2ljkrfl8icjtx" />
                </td>
                <td className="max-w-[180px] px-4 py-3" data-id="element-383" data-mp="2gpq7cgozq2ub">
                  <ParentCell name={student.motherName} phone={student.motherPhone} data-id="element-384" data-mp="2broonwvh85jd" />
                </td>
                <td className="max-w-[220px] px-4 py-3" data-id="element-385" data-mp="26twpqiwezj5o">
                  <p className="truncate text-sm text-muted-foreground" data-id="element-386" data-mp="26rajcml9f9p1">
                    {student.note.trim() ? truncate(student.note, 42) : '—'}
                  </p>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-387" data-mp="2zfpswtlmgzsz">
                  <RowActions student={student} data-id="element-388" data-mp="2uhocyhjhtglq" />
                </td>
              </motion.tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="space-y-3 p-4 md:hidden" data-id="element-389" data-mp="1j42eg5rqu2nj">
        {students.map(student => <li key={student.id} className="rounded-2xl border border-[var(--glass-hairline)] bg-white/55 p-4 dark:bg-white/5" data-id="element-390" data-mp="1e4przm4saxlh">
            <div className="flex items-start gap-3" data-id="element-391" data-mp="3ptmgib01c8k8">
              <Monogram student={student} size="lg" data-id="element-392" data-mp="3uq4jp6fpvyug" />
              <div className="min-w-0 flex-1" data-id="element-393" data-mp="3uothhb1x6z5g">
                <button type="button" onClick={() => openDetail(student)} className="block max-w-full truncate text-left text-sm font-semibold text-foreground" data-id="element-394" data-mp="3kbggbd0pkd5">
                  {orDash(student.fullNames)}
                </button>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-id="element-395" data-mp="hb85js7qjjfg">
                  <GenderBadge student={student} data-id="element-396" data-mp="mbcgl2pvjlya" />
                  {student.className.trim() && <Badge variant="secondary" className="rounded-full" data-id="element-397" data-mp="rapcse2my5dj">
                      {student.className}
                    </Badge>}
                </div>
              </div>
              <RowActions student={student} data-id="element-398" data-mp="2t10qnp76zg68" />
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--glass-hairline)] pt-3" data-id="element-399" data-mp="2t3n1pytakdt7">
              <div className="min-w-0" data-id="element-400" data-mp="yrc2j9az7y40">
                <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-401" data-mp="tvdjm18f38az">Father</dt>
                <dd className="mt-0.5" data-id="element-402" data-mp="18igkmhvy7z32">
                  <ParentCell name={student.fatherName} phone={student.fatherPhone} data-id="element-403" data-mp="18lcgm0o8kdov" />
                </dd>
              </div>
              <div className="min-w-0" data-id="element-404" data-mp="fuklz7mtjx3n">
                <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-405" data-mp="fvm39tufhftt">Mother</dt>
                <dd className="mt-0.5" data-id="element-406" data-mp="axkfwwpyyucj">
                  <ParentCell name={student.motherName} phone={student.motherPhone} data-id="element-407" data-mp="psqqzy3x9jcq" />
                </dd>
              </div>
            </dl>

            {student.note.trim() && <p className="mt-3 rounded-xl bg-white/60 p-2.5 text-xs text-muted-foreground dark:bg-white/5" data-id="element-408" data-mp="kw9xsphv8ess">
                {truncate(student.note, 120)}
              </p>}
          </li>)}
      </ul>
    </>;
}